import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  defer,
  filter,
  firstValueFrom,
  fromEvent,
  iif,
  interval,
  map,
  of,
  race,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { AppService } from 'src/app/app.service';
import { HelpCenterService } from 'src/app/pages/help-center/help-center.service';
import { LoginService } from 'src/app/pages/login-dialog/login.service';
import { RegisterAgreeComponent } from 'src/app/pages/login-dialog/register-agree/register-agree.component';
// 兩者僅作為型別參數使用，必須是 import type —— 改回 value import 會讓兩個彈窗
// 連同其相依重新回到 initial bundle（本服務為 providedIn:'root' 且被 app.component 注入）。
import type { KycDialogComponent } from 'src/app/shared/components/kyc-dialog/kyc-dialog.component';
import type { Verify2FADialogComponent } from 'src/app/shared/components/verify-2fa/verify-2fa.dialog';
import { KycDialog, Verify2FADialog } from 'src/app/shared/service/dialog-loaders';
import { LazyDialogService } from 'src/app/shared/service/lazy-dialog.service';
import { AccountApi } from '../../apis/account.api';
import { AuthApi } from '../../apis/auth.api';
import { MemberBindSocialUserParams } from '../../interfaces/account.interface';
import {
  SocialUserLoginData,
  SocialUserLoginParam,
  SocialUserVerifyParams,
  ThirdAuthUser,
  VerifyAction,
  Web3WalletActionType,
  Web3WalletAuthParams,
  Web3WalletType,
} from '../../interfaces/auth.interface';
import { ResponseData } from '../../interfaces/response.interface';
import { BroadcastChannelService } from '../../service/broadcast-channel.service';
import {
  MatConnectedDialog,
  MatConnectedDialogMobileAnimation,
  MatConnectedDialogRef,
} from '../../service/connected-dialog';
import { LocaleService } from '../../service/locale.service';
import { LocalStorageService } from '../../service/localstorage.service';
import { SentryService } from '../../service/sentry.service';
import { ToastService } from '../../service/toast.service';
import { Web3WalletService } from '../../service/web3-wallets/web3-wallet.service';
import { CsPopupService } from '../cs-popup/cs-popup.service';
import { TimeoutCountdownDialogComponent } from '../timeout-countdown-dialog/timeout-countdown-dialog.component';

export type OtherLogins = {
  name: ThirdAuthUser;
  pic: string;
  src: string;
  origin: string;
};

type Data = {
  content: string;
  description: string;
};

type TThirdPartyCallback = (params?: SocialUserLoginParam) => void;

type TAuthOptions = {
  authName: ThirdAuthUser;
  isRegLogPage?: boolean;
  afterThirdAuthCallback?: TThirdPartyCallback;
  pubsMemberBindSocialUserExtraParams?: { otpType: VerifyAction; key: string; userType: ThirdAuthUser };
};
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ThirdAuthM8Service {
  constructor(
    private appService: AppService,
    private authApi: AuthApi,
    private accountApi: AccountApi,
    private localeService: LocaleService,
    private sentryService: SentryService,
    private matConnectedDialog: MatConnectedDialog,
    private lazyDialog: LazyDialogService,
    private localstorageService: LocalStorageService,
    private csPopupService: CsPopupService,
    private toast: ToastService,
    private web3WalletService: Web3WalletService,
    private broadcastChannelService: BroadcastChannelService,
    private loginService: LoginService,
    private helpCenterService: HelpCenterService,
  ) {
    // 订阅第三方验证之后 的操作
    this.afterVerify$.pipe(untilDestroyed(this)).subscribe(response => {
      if (this.errorProcess(response)) return;

      const { data, socialUserType } = response;

      // 通過驗證，且已註冊 - 登入成功
      if (data?.isRegister && data?.isVerified && data?.socialUserId) {
        if (data?.loginResult) {
          // 直接登录 做登录操作
          const loginResult = data.loginResult;
          this.loginService.logged(loginResult?.token || '');
        }
        return;
      }

      // 通過驗證，但未註冊
      if (!data?.isRegister && data?.isVerified) {
        const actionFrom = this.actionFrom$.value;

        const handleExecuteCreateNewAccount = async () => {
          // 未註冊的第三方帳號，依據 needKyc 決定後續流程
          const socialUserId = data.socialUserId;

          const needKyc = await this.loginService.refreshNeedKycFlag();
          if (needKyc) {
            // KYC 流程包含條款確認，不再顯示 Agree component
            this.openThirdPartyKycFlow(socialUserId, socialUserType, data?.socialUserEmail);
          } else {
            const callback = () => {
              void this.registerSocialAccount(socialUserId, socialUserType, data?.socialUserEmail);
            };
            void this.onThirdPopup(callback);
          }
        };
        // 如果是從登入頁面觸發，則顯示確認視窗
        if (actionFrom === 'login') {
          // Show the account creation confirmation modal
          this.csPopupService.confirm({
            header: this.localeService.getValue('account_creation'),
            title: this.localeService.getValue('do_you_want_to_create_a_new_account'),
            message: this.localeService.getValue('this_login_method_has_not_been_linked_to_a_player'),
            cancelText: this.localeService.getValue('cancel'),
            confirmCallback: () => handleExecuteCreateNewAccount(),
          });
        } else {
          handleExecuteCreateNewAccount();
        }
      }
    });
  }

  /**统一弹窗名字 */
  thirdAuthPopup: string = 'THIRD_AUTH_POPUP';

  /**获取统一弹窗尺寸描述字符串 */
  getPopupConfig() {
    const popupHeight = 600;
    const popupWidth = 600;
    const top = window.screen.height / 2 - popupHeight / 2;
    const left = window.screen.width / 2 - popupWidth / 2;
    return `width=${popupWidth},height=${popupHeight},top=${top},left=${left},resizable=yes`;
  }

  get thirdAuthLink(): any {
    return JSON.parse(this.appService.tenantConfig$.value?.config?.thirdAuth || '{}');
  }

  /** 解绑所需要的 数据 */
  unBindSocialData$ = new BehaviorSubject<{} | null>(null);

  /** 第三方验证完后调用我们的接口 */
  afterVerify$: Subject<ResponseData<SocialUserLoginData>> = new Subject();

  /** 在安全中心绑定账号 */
  afterVerifyInSafeHome$: Subject<{ status: boolean; socialUserType: string; message: string }> = new Subject();

  /** 用户第三方登录后 选择注册 */
  userRegister: boolean = false;

  /** 从登录跳转到注册 页面把参数传过去 */
  verifyData!: ResponseData<SocialUserLoginData>;

  /** 已绑定且注册的用户 在注册页面中点击第三方注册 需要回到登录进行自动登录  */
  userLogin: boolean = false;

  /** 觸發第三方驗證的來源 */
  actionFrom$ = new BehaviorSubject<string>('');

  /**
   * 開啟一個新的彈出視窗，並監聽其結果或關閉事件。
   * @param url 要在彈出視窗中開啟的 URL
   * @param windowName 視窗的名稱
   * @param popupConfig 視窗的外觀和尺寸選項
   * @param originMessageValidator 子視窗來源驗證器
   * @returns 一個 Observable，它會在以下情況發出值並結束：
   * - 收到來自彈出視窗的訊息時，發出該訊息資料。
   * - 彈出視窗被使用者關閉時，發出 `null`。
   * - 如果彈出視窗被阻擋而無法開啟，則會發出一個錯誤。
   */
  private _popupWindowOpenAndWatch(
    url: string,
    windowName = '_blank',
    popupConfig?: string,
    originMessageValidator?: (event: MessageEvent) => boolean,
  ): Observable<MessageEvent | null> {
    // 開啟新視窗，並處理彈出視窗被阻擋的情況
    const newWindow = window.open(url, windowName, popupConfig);
    if (!newWindow) {
      return throwError(() => new Error('Popup window was blocked or failed to open.'));
    }

    // 讓新視窗獲得焦點
    newWindow.focus();

    // 設定訊息來源驗證器
    const filterValidator = (event: MessageEvent) => {
      if (originMessageValidator) {
        return originMessageValidator(event);
      }
      return event.source === newWindow || (event.source as any)?.location?.href?.includes('thirdauth');
    };

    // 監聽來自子視窗的 'message' 事件 (只接受來自我們剛剛開啟的視窗的訊息)
    const message$ = fromEvent<MessageEvent>(window, 'message').pipe(
      filter(event => filterValidator(event)),
      map(event => (event.data?.error ? null : event)), // data 回傳 error 視為取消
      take(1),
    );

    // 監聽來自 BroadcastChannel 的 'message' 事件 (當 Window.opener 無效時的備援方案)
    const channel$ = this.broadcastChannelService.listen<MessageEvent>(windowName).pipe(take(1));

    // 定期檢查子視窗是否已被關閉 (持續檢查 newWindow.closed 屬性，如果已關閉，則發出 null 作為訊號)
    const closed$ = interval(500).pipe(
      filter(() => newWindow.closed),
      map(() => null),
      take(1),
    );

    // race 會回傳第一個發出事件的 Observable 的結果，並自動退訂另一個
    return race(message$, channel$, closed$);
  }

  /** 第三方驗證返回後 Token 處理，在注册/登录界面调用 */
  private _verifyLogin(params: SocialUserLoginParam): void {
    this.authApi
      .verifyLogin(params)
      .subscribe(data => this.afterVerify$.next({ ...data, socialUserType: params.userType }));
  }

  /** 第三方驗證返回後 Token 處理，在安全中心绑定 */
  private _socialUserBind(params: SocialUserLoginParam): void {
    this.authApi.socialUserBind(params).subscribe(response =>
      this.afterVerifyInSafeHome$.next({
        status: response.data,
        socialUserType: params.userType,
        message: response.message,
      }),
    );
  }

  private _pubsMemberBindSocialUser(params: MemberBindSocialUserParams): void {
    this.accountApi.pubsMemberBindSocialUser(params).subscribe(response => {
      if (response.success) {
        this.afterVerifyInSafeHome$.next({
          status: response.data,
          socialUserType: params.userType,
          message: response.message,
        });
      }
    });
  }

  private _handleAfterThirdAuth(
    localParams: SocialUserLoginParam,
    { isRegLogPage, afterThirdAuthCallback, pubsMemberBindSocialUserExtraParams }: Omit<TAuthOptions, 'authName'>,
  ) {
    if (afterThirdAuthCallback) {
      afterThirdAuthCallback(localParams);
      return;
    }

    if (pubsMemberBindSocialUserExtraParams) {
      this._pubsMemberBindSocialUser({ ...localParams, ...pubsMemberBindSocialUserExtraParams });
      return;
    }

    if (isRegLogPage) {
      this._verifyLogin(localParams);
    } else {
      this._socialUserBind(localParams);
    }
  }

  /**
   * google验证
   *
   * @param isRegLogPage 是不是在注册或者登录页面
   */
  googleAuth({ isRegLogPage = true, afterThirdAuthCallback, pubsMemberBindSocialUserExtraParams }: TAuthOptions) {
    if (!this.thirdAuthLink.googleLink) return;

    const popup = window.open(
      this.thirdAuthLink.googleLink.replace('{{DOMAIN}}', window.location.origin),
      this.thirdAuthPopup,
      this.getPopupConfig(),
    );

    if (!popup) {
      this.toast.show({
        message: this.localeService.getValue('popup_was_blocked_please_allow_popups_in_your_browser'),
        type: 'fail',
      });
      return;
    }

    popup.focus();

    const googleMessage = (event: MessageEvent) => {
      try {
        // 避免非 popup 傳來的訊息
        if (!event?.source || !(event.source as Window)?.location?.href?.includes('thirdauth')) return;
        if (!event?.data) return;

        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const { code, state } = data || {};
        if (!code || !state) return;

        // 拿到合法資料後，解除監聽
        window.removeEventListener('message', googleMessage);

        const callBackUrl = `${window.location.origin}/thirdauth/index.html`;
        const params: SocialUserLoginParam = { callBackUrl, code, state, userType: ThirdAuthUser.Google };

        this._handleAfterThirdAuth(params, {
          isRegLogPage,
          afterThirdAuthCallback,
          pubsMemberBindSocialUserExtraParams,
        });
      } catch (err) {
        console.error('Google auth message parse error:', err);
      }
    };

    window.addEventListener('message', googleMessage, false);
  }

  /**
   * 狐狸验证
   *
   * @param isRegLogPage  是不是在注册或者登录页面
   */
  metaAuth({ isRegLogPage = true }: TAuthOptions) {
    /** 动态引用web3的js */
    this.appService.loadExternalScript(`assets/scripts/web3.min.js`, `Web3`, () => {
      // @ts-ignore
      if (window.MetaMaskSDK.MetaMaskSDK) {
        // @ts-ignore
        const MMSDK = new window.MetaMaskSDK.MetaMaskSDK();
        const ethereum = MMSDK.getProvider();
        ethereum
          .request({ method: 'eth_requestAccounts' })
          .then((result: any) => {
            const address = result[0];
            this.authApi.getNonce(address).subscribe(nonceRes => {
              const nonce = nonceRes.data;
              if (nonce) {
                // @ts-ignore
                ethereum
                  .request({
                    method: 'personal_sign',
                    params: [nonce, address],
                  })
                  .then((signature: any) => {
                    if (signature) {
                      //在登录或者注册页面调用
                      if (isRegLogPage) {
                        this.authApi
                          .verifyLogin({
                            userType: ThirdAuthUser.MetaMask,
                            signature: signature, //签字
                            address: address, //地址
                          })
                          .subscribe({
                            next: data => {
                              this.afterVerify$.next({ ...data, socialUserType: 'MetaMask' });
                            },
                            error: (error: any) => {
                              // 处理错误
                              console.error('Error during MetaMask login:', error);
                              this.errorProcess(error);
                            },
                          });
                      }
                      // 在安全中心绑定
                      if (!isRegLogPage) {
                        this.authApi
                          .socialUserBind({
                            userType: ThirdAuthUser.MetaMask,
                            signature: signature, //签字
                            address: address, //地址
                          })
                          .subscribe(response =>
                            this.afterVerifyInSafeHome$.next({
                              status: response.data,
                              socialUserType: ThirdAuthUser.MetaMask,
                              message: response.message,
                            }),
                          );
                      }
                    }
                  });
              }
            });
          })
          .catch((error: any) => {
            if (error?.code === -32002) {
              this.toast.show({
                message: this.localeService.getValue('metamask_lock'),
                type: 'fail',
              });
            }
            if (error?.code === 4100) {
              this.toast.show({
                message: this.localeService.getValue('no_permission'),
                type: 'fail',
              });
            }
            const web3 = document.getElementById('Web3');
            web3?.remove();
          });
      } else {
        if (!this.thirdAuthLink.metaLink) return;
        window.open(this.thirdAuthLink.metaLink, '__blank');
      }
    });
  }

  /**
   * Line 验证
   *
   * @param isRegLogPage  是不是在注册或者登录页面
   */
  lineAuth({ isRegLogPage = true }: TAuthOptions) {
    if (!this.thirdAuthLink.lineLink) return;

    this._popupWindowOpenAndWatch(
      this.thirdAuthLink.lineLink.replace('{{DOMAIN}}', window.location.origin),
      this.thirdAuthPopup,
      this.getPopupConfig(),
    )
      .pipe(filter(e => e !== null))
      .subscribe(event => {
        const { code, state } = event?.data || {};
        if (!code || !state) return;

        const callBackUrl = `${window.location.origin}/thirdauth/index.html`;
        const params: SocialUserLoginParam = { callBackUrl, code, state, userType: ThirdAuthUser.Line };
        if (isRegLogPage) {
          this._verifyLogin(params);
        }
        if (!isRegLogPage) {
          this._socialUserBind(params);
        }
      });
  }

  telegramReceive$!: Subscription;

  /**
   * telegram 验证
   *
   * - 該 popup 由元件自身 template iframe 自行呼叫，以確保 window 可以監聽傳回之 message
   * @param isRegLogPage 是不是在注册或者登录页面
   * @param callback
   */
  telegramAuth({ isRegLogPage = true, afterThirdAuthCallback, pubsMemberBindSocialUserExtraParams }: TAuthOptions) {
    if (!this.thirdAuthLink.telegramLink) return;

    // 移除意外關閉而無法取消監聽問題
    this.telegramReceive$?.unsubscribe();

    this.telegramReceive$ = fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter(event => typeof event.data === 'string'),
        take(1),
      )
      .subscribe({
        next: (event: MessageEvent) => {
          const data = JSON.parse(event?.data || '{}');
          if (!data) return;

          // 对比验证中心的地址
          const origin = this.thirdAuthLink.telegramLink.split('origin=')[1].split('&')[0];
          if (data.origin !== origin) return;
          if (!data.result) return;

          const params: SocialUserLoginParam = { additionalProp: data.result, userType: ThirdAuthUser.Telegram };

          this._handleAfterThirdAuth(params, {
            isRegLogPage,
            afterThirdAuthCallback,
            pubsMemberBindSocialUserExtraParams,
          });
        },
        error: (error: any) => {
          console.error('Telegram auth message subscription error:', error);
        },
      });
  }

  /**
   * facebook 验证
   *
   * @param {boolean} isRegLogPage 是不是在注册或者登录页面
   */
  facebookAuth({
    isRegLogPage = true,
    afterThirdAuthCallback,
    pubsMemberBindSocialUserExtraParams,
  }: TAuthOptions): void {
    if (!this.thirdAuthLink.facebookLink) return;

    this._popupWindowOpenAndWatch(
      this.thirdAuthLink.facebookLink.replace('{{DOMAIN}}', window.location.origin),
      this.thirdAuthPopup,
      this.getPopupConfig(),
    )
      .pipe(filter(e => e !== null))
      .subscribe(event => {
        const { code, state } = event?.data || {};
        if (!code || !state) return;

        const callBackUrl = `${window.location.origin}/thirdauth/index.html`;
        const params: SocialUserLoginParam = { callBackUrl, code, state, userType: ThirdAuthUser.Facebook };
        this._handleAfterThirdAuth(params, {
          isRegLogPage,
          afterThirdAuthCallback,
          pubsMemberBindSocialUserExtraParams,
        });
      });
  }

  /**
   * x (twitter) 验证
   *
   * @param {boolean} isRegLogPage 是不是在注册或者登录页面
   */
  xAuth({ isRegLogPage = true, afterThirdAuthCallback, pubsMemberBindSocialUserExtraParams }: TAuthOptions): void {
    if (!this.thirdAuthLink.xLink) return;

    const popup = window.open(
      this.thirdAuthLink.xLink.replace('{{DOMAIN}}', window.location.origin),
      this.thirdAuthPopup,
      this.getPopupConfig(),
    );

    if (!popup) {
      this.toast.show({
        message: this.localeService.getValue('popup_was_blocked_please_allow_popups_in_your_browser'),
        type: 'fail',
      });
      return;
    }

    popup.focus();

    const xMessage = (event: MessageEvent) => {
      try {
        // 避免非 popup 傳來的訊息
        if (!event?.source || !(event.source as Window)?.location?.href?.includes('thirdauth')) return;
        if (!event?.data) return;

        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const { code, state } = data || {};
        if (!code || !state) return;

        // 拿到合法資料後，解除監聽
        window.removeEventListener('message', xMessage);

        const callBackUrl = `${window.location.origin}/thirdauth/index.html`;
        const params: SocialUserLoginParam = { callBackUrl, code, state, userType: ThirdAuthUser.X };

        this._handleAfterThirdAuth(params, {
          isRegLogPage,
          afterThirdAuthCallback,
          pubsMemberBindSocialUserExtraParams,
        });
      } catch (err) {
        console.error('X auth message parse error:', err);
      }
    };

    window.addEventListener('message', xMessage, false);
  }

  /**
   * coinbase
   *
   * @param isRegLogPage 是不是在注册或者登录页面
   */
  coinbaseAuth({ isRegLogPage = true }: TAuthOptions) {
    if (!this.thirdAuthLink.coinbaseLink) return;

    this._popupWindowOpenAndWatch(
      this.thirdAuthLink.coinbaseLink.replace('{{DOMAIN}}', window.location.origin),
      this.thirdAuthPopup,
      this.getPopupConfig(),
    )
      .pipe(filter(e => e !== null))
      .subscribe(event => {
        const { code, state } = event?.data || {};
        if (!code || !state) return;

        const callBackUrl = `${window.location.origin}/thirdauth/index.html`;
        const params: SocialUserLoginParam = { callBackUrl, code, state, userType: ThirdAuthUser.Coinbase };
        if (isRegLogPage) {
          this._verifyLogin(params);
        }
        if (!isRegLogPage) {
          this._socialUserBind(params);
        }
      });
  }

  /**
   * 错误处理
   *
   * @param response 社交验证后返回的 数据
   * @returns boolean
   */
  errorProcess(response: ResponseData<SocialUserLoginData>): boolean {
    if (!response) {
      this.sentryService.error('LoginError', 'Third-party Login Verify Error', {
        extra: { response },
        level: 'warning',
      });
    }

    // 第三方登录频繁 统一处理
    if (response?.code === '2130') {
      this.toast.show({
        message: response?.message || this.localeService.getValue('too_many_login_attempts_please_try_again_later'),
        type: 'fail',
      });
      return true;
    }

    //code 2108 - 暂不支持此社交账号登录;
    if (response?.code === '2108') {
      this.toast.show({
        message: response?.message || this.localeService.getValue('this_social_account_login_is_not_supported_yet'),
        type: 'fail',
      });
      return true;
    }

    // code 2012 账户被锁
    if (response?.code === '2012') {
      this.toast.show({
        message: response?.message ||
          this.localeService.getValue('your_account_has_been_locked_please_contact_customer_support'),
        type: 'fail',
      });
      return true;
    }

    if (response?.code === '2123') {
      this.toast.show({
        message:
          response?.message ||
          this.localeService.getValue('you_are_in_a_self_exclusion_period_please_contact_customer'),
        type: 'fail',
      });
      return true;
    }
    // 2124 用户处于 Responsible Gaming timeout 状态
    if (response?.code === '2124') {
      const { endUnixTimestamp, period, reopenKey, reopenUnixTimestamp, startUnixTimestamp } =
        response?.data?.loginResult?.memberTimeout || {};
      this.matConnectedDialog.closeAll();
      this.matConnectedDialog.open(TimeoutCountdownDialogComponent, {
        data: {
          startUnixTimestamp,
          endUnixTimestamp,
          reopenUnixTimestamp,
          period,
          reopenKey,
        },
        disableClose: true,
      });
      return true;
    }

    // code 2013 禁止登入
    if (response?.code === '2013') {
      this.helpCenterService.showUnableLoginPopup();
      return true;
    }

    // 第三方验证 未通过 - 提示更换第三方账号
    if (!response?.data?.isVerified) {
      // 提示 更换账号 弹窗提示
      this.toast.show({
        message: this.localeService.getValue('verification_failed_please_use_a_different_third_party_account_or'),
        type: 'fail',
      });
      return true;
    }

    return false;
  }

  /**
   *
   * 第三方 弹窗 (提示用户：確認註冊條款)
   *
   * @param footerTemplate
   * @param callback
   */
  async onThirdPopup(callback: (() => void)[] | (() => void)): Promise<void> {
    this.matConnectedDialog.closeAll();

    this.matConnectedDialog
      .open(RegisterAgreeComponent, {
        data: {
          callback,
          closeAfterSubmit: true, // 提交后关闭弹窗
        },
        mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
        mobileFixedBottom: true,
      })
      .afterClosed()
      .pipe(filter(isRegisterAgree => !isRegisterAgree))
      .subscribe(() => this.web3WalletService.disconnectWallet());
  }

  /**重置一些状态 */
  reset() {
    this.telegramReceive$?.unsubscribe();
  }

  private async registerSocialAccount(
    socialUserId: string,
    socialUserType: ThirdAuthUser,
    email: string,
    otpCode?: string,
  ): Promise<boolean> {
    // 封裝 registerbysocial 流程，方便不同註冊路徑共用
    try {
      const socialUserEmail = email || '';
      const aff = this.localstorageService.myAffiliate ?? undefined;
      const response = await firstValueFrom(
        this.authApi.onSoicalByPass({ socialUserId, socialUserType, socialUserEmail, aff }),
      );
      if (response?.success) {
        const token = response?.data || null;
        if (token) {
          // needKyc=true 時，會帶入 email 與 otpCode 進行 liteKyc
          if (otpCode) {
            this.appService.updateToken(token);
            await this.loginService.triggerLiteKyc(email, otpCode);
          }
          // needKyc=false 時，直接登入
          this.localstorageService.isNewRegisterLogin = true;
          this.toast.show({ message: this.localeService.getValue('register_success'), type: 'success' });
          this.loginService.logged(token, true);
          await this.pushSuccessRegistration();
          return true;
        } else {
          this.toast.show({
            message: this.localeService.getValue('registration_failed_no_token_received'),
            type: 'fail',
          });
        }
      } else {
        this.toast.show({ message: response?.message || this.localeService.getValue('registration_failed'), type: 'fail' });
      }
      return false;
    } catch (error: any) {
      this.toast.show({ message: error?.message || this.localeService.getValue('registration_failed'), type: 'fail' });
      console.error(error);
      return false;
    }
  }

  private async pushSuccessRegistration(): Promise<void> {
    try {
      this.localstorageService.registrationSuccessPending = true;
    } catch (error) {
      console.error('pushSuccessRegistration (third-auth) failed', error);
    }
  }

  /** 同步外殼：彈窗改為延遲載入後本流程成為非同步，此處以 void 丟棄 Promise，避免 async 外溢到 8 個呼叫端。 */
  private openThirdPartyKycFlow(socialUserId: string, socialUserType: ThirdAuthUser, socialUserEmail?: string): void {
    void this.runThirdPartyKycFlow(socialUserId, socialUserType, socialUserEmail);
  }

  private async runThirdPartyKycFlow(
    socialUserId: string,
    socialUserType: ThirdAuthUser,
    socialUserEmail?: string,
  ): Promise<void> {
    // needKyc=true 時：先進 KYC 並帶入 T&C checkbox
    this.matConnectedDialog.closeAll();
    const dialogRef = await this.lazyDialog.open<KycDialogComponent, unknown, any>(KycDialog, {
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });

    // chunk 載入失敗：使用者會停在「已連錢包但沒有進 KYC」的中間狀態，
    // 與使用者主動取消 KYC 一樣處理，斷開錢包連線讓流程回到起點。
    if (!dialogRef) {
      this.web3WalletService.disconnectWallet();
      return;
    }

    if (dialogRef.componentInstance) {
      const userEmail =
        socialUserType === ThirdAuthUser.Google || socialUserType === ThirdAuthUser.X ? socialUserEmail || '' : '';
      const hasEmail = !!userEmail;
      dialogRef.componentInstance.requireTerms = true;
      dialogRef.componentInstance.requireEmail = !hasEmail;
      dialogRef.componentInstance.prefillEmail = userEmail;
      dialogRef.componentInstance.submitted.pipe(take(1)).subscribe((formValue: any) => {
        const resolvedEmail = hasEmail ? userEmail : formValue.email;
        this.loginService.kycFormValue = {
          countryCode: formValue.country,
          fullName: `${formValue.name} ${formValue.surname}`,
          firstName: formValue.name,
          lastName: formValue.surname,
          dob: formValue.dateOfBirth,
          address: formValue.street,
          city: formValue.city,
          zipCode: formValue.postcode,
          email: resolvedEmail,
          // Third-party sign-up also creates a new player. Persist the phone
          // selected in Basic KYC because no Security-verified phone exists.
          areaCode: formValue.areaCode,
          mobile: formValue.phone,
        };
        dialogRef.close({ ...formValue, email: resolvedEmail });
      });
    }

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        this.web3WalletService.disconnectWallet();
        return;
      }

      const email = typeof result?.email === 'string' ? result.email : '';
      this.openThirdParty2faDialog(email, socialUserId, socialUserType);
    });
  }

  private async openThirdParty2faDialog(
    email: string,
    socialUserId: string,
    socialUserType: ThirdAuthUser,
  ): Promise<void> {
    // KYC 完成後進行二階段驗證，成功才最終呼叫 registerbysocial
    let dialogRef: MatConnectedDialogRef<Verify2FADialogComponent, boolean> | null;
    const submitFunc = async (code: any) => {
      const success = await this.registerSocialAccount(socialUserId, socialUserType, email, code);
      if (success) {
        dialogRef?.close(true);
      } else {
        this.web3WalletService.disconnectWallet();
      }
      return success;
    };

    dialogRef = await this.lazyDialog.open<Verify2FADialogComponent, unknown, boolean>(Verify2FADialog, {
      data: {
        header: this.localeService.getValue('email_verification_title'),
        title: this.localeService.getValue('two_step_verification'),
        submitText: this.localeService.getValue('confirm_email'),
        otpType: 'Register',
        isSpecificType: true,
        email,
        submitFunc,
      },
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });

    // chunk 載入失敗：註冊無法完成，與 KYC 那段一致地讓流程回到起點。
    if (!dialogRef) {
      this.web3WalletService.disconnectWallet();
    }
  }

  /**
   * 驗證從社群平台登入是否成功
   * telegram mini app
   *
   * @param params
   */
  socialUserVerify(params: SocialUserVerifyParams) {
    const { userType } = params;

    this.authApi
      .socialUserVerify(params)
      .subscribe(data => this.afterVerify$.next({ ...data, socialUserType: userType }));
  }

  /**
   * Web3 Wallet 驗證
   *
   * @param Web3WalletAuthParams web3 Wallet 驗證參數
   * @description
   * 在存提款頁面(otpType = 'Deposit')的綁定不需要2FA
   * 在Funds頁面(otpType = 'BindSocial')的綁定改為不需要2FA，但保留otpType參數以區分
   */
  async web3WalletAuth(params: Web3WalletAuthParams) {
    const { web3WalletType, isRegLogPage, otpType, key = '' } = params;

    // 連接 Web3 Wallet
    await this.web3WalletService.connectWallet(web3WalletType);

    // 確認 Web3 Wallet 是否正確連接
    const accounts = await firstValueFrom(this.web3WalletService.accounts$);
    const address = accounts[0];
    if (!address) {
      this.web3WalletService.updateConnectedStatus(false);
      return;
    }

    // 處理（後端）帳號授權
    const handleAcctAuthorization = (actionType: Web3WalletActionType) => {
      this.authApi
        .getWeb3WalletNonce({ address, walletType: web3WalletType, actionType })
        .pipe(
          map(nonceRes => nonceRes.data),
          switchMap(nonce =>
            iif(
              () => !!nonce,
              defer(() => of(nonce)),
              throwError(() => new Error()),
            ),
          ),
        )
        .subscribe({
          next: nonce => handleSignatureModal(nonce, actionType),
          error: () => this.web3WalletService.updateConnectedStatus(false),
        });
    };

    const handleSignatureModal = (nonce: string, actionType: Web3WalletActionType) => {
      this.csPopupService.confirm({
        header: this.localeService.getValue('signature_required'),
        message: this.localeService.getValue('please_approve_the_signature_request_in_your_web3_wallet_in'),
        confirmText: this.localeService.getValue('approve_signature'),
        cancelText: '',
        textAlign: 'center',
        disableClose: true,
        confirmCallback: () => handleSignature(nonce, actionType),
      });
    };

    // 處理錢包簽章
    const handleSignature = async (nonce: string, actionType: Web3WalletActionType) => {
      const signatureInfo = await this.web3WalletService.signMessage(nonce);
      if (!signatureInfo) {
        this.web3WalletService.updateConnectedStatus(false);
        return;
      }

      switch (actionType) {
        case Web3WalletActionType.Login:
          this.authApi.web3WalletLogin(signatureInfo).subscribe({
            next: data => {
              this.afterVerify$.next({ ...data, socialUserType: web3WalletType });
              this.web3WalletService.updateConnectedStatus(true);
            },
            error: (error: any) => {
              console.error(`Error during ${web3WalletType} login:`, error);
              this.errorProcess(error);
              this.web3WalletService.updateConnectedStatus(false);
            },
          });
          break;

        case Web3WalletActionType.Binding: {
          const params: MemberBindSocialUserParams = {
            key,
            otpType,
            address,
            userType: web3WalletType as unknown as ThirdAuthUser,
            network: signatureInfo.network, // Chain ID
            signature: signatureInfo.signature, // Web3錢包回應的簽章Key
            friendlyAddress: signatureInfo?.friendlyAddress,
            publicKey: signatureInfo?.publicKey,
            domain: signatureInfo?.domain,
            timestamp: signatureInfo?.timestamp,
          };
          this.accountApi.pubsMemberBindSocialUser(params).subscribe(({ success, message, data }) => {
            if (success) {
              // web3 綁定成功
              this.afterVerifyInSafeHome$.next({
                status: data,
                socialUserType: web3WalletType,
                message,
              });

              this.web3WalletService.updateConnectedStatus(true);
              this.csPopupService.success({
                header: this.localeService.getValue('account_connection'),
                title: `${web3WalletType} Successful`,
                message: this.localeService.getValue('you_have_successfully_enabled_a_new_login_method_for_your_2'),
                confirmText: this.localeService.getValue('close'),
              });
            } else {
              // web3 綁定失敗
              this.web3WalletService.updateConnectedStatus(false);
              this.csPopupService.error({
                header: this.localeService.getValue('account_connection'),
                title: this.localeService.getValue('existing_account_link_detected'),
                message: this.localeService.getValue('this_login_method_is_linked_to_an_existing_account_please'),
                confirmText: this.localeService.getValue('close'),
              });
            }
          });
          break;
        }

        default:
          break;
      }
    };

    switch (isRegLogPage) {
      // 在登入或註冊畫面
      case true: {
        handleAcctAuthorization(Web3WalletActionType.Login);
        break;
      }

      // 在安全中心或web3綁定畫面
      case false: {
        this.accountApi.getWeb3Wallet().subscribe(res => {
          const connectedWallets = res.data || [];
          const isExistingWallet = connectedWallets.some(
            wallet => wallet.address.toLowerCase() === address.toLowerCase(),
          );

          if (isExistingWallet) {
            this.web3WalletService.updateConnectedStatus(true);
          } else {
            handleAcctAuthorization(Web3WalletActionType.Binding);
          }
        });
        break;
      }

      default:
        break;
    }
  }

  buildThirdPartyInfo(filters: ThirdAuthUser[]) {
    const openedSocialLogin = this.appService.tenantConfig$.value?.openedSocialLogin ?? [];
    return filters
      .filter((item: OtherLogins['name']) => openedSocialLogin.includes(item))
      .map((item: OtherLogins['name']) => {
        let src = '';
        let origin = '';
        if (item === 'Telegram') {
          const telegramLink = this.thirdAuthLink.telegramLink;
          origin = telegramLink ? telegramLink.split('origin=')[1].split('&')[0] : telegramLink;
          const open = encodeURIComponent(telegramLink);
          const config = encodeURIComponent(this.getPopupConfig());
          src = `${origin}?open=${open}&config=${config}`;
        }
        return {
          name: item,
          pic: `/assets/images/login-wallets/${item.toLowerCase()}.svg`,
          src: src,
          origin: origin,
        } as OtherLogins;
      });
  }

  /**
   * 第三方验证
   *
   * @param options
   */
  handleAuth(options: TAuthOptions): void {
    const { authName, isRegLogPage = true } = options;
    switch (authName) {
      case ThirdAuthUser.Google:
        this.googleAuth(options);
        break;

      case ThirdAuthUser.Line:
        this.lineAuth(options);
        break;

      case ThirdAuthUser.Telegram:
        this.telegramAuth(options);
        break;

      case ThirdAuthUser.Facebook:
        this.facebookAuth(options);
        break;

      case ThirdAuthUser.X:
        this.xAuth(options);
        break;

      case ThirdAuthUser.Coinbase:
        this.coinbaseAuth(options);
        break;

      case ThirdAuthUser.CoinbaseWallet:
        this.web3WalletAuth({
          web3WalletType: Web3WalletType.CoinbaseWallet,
          otpType: 'Deposit',
          isRegLogPage,
        });
        break;

      case ThirdAuthUser.WalletConnect:
        this.web3WalletAuth({
          web3WalletType: Web3WalletType.WalletConnect,
          otpType: 'Deposit',
          isRegLogPage,
        });
        break;

      case ThirdAuthUser.TonConnect:
        this.web3WalletAuth({
          web3WalletType: Web3WalletType.TonConnect,
          otpType: 'Deposit',
          isRegLogPage,
        });
        break;

      case ThirdAuthUser.MetaMask:
        this.web3WalletAuth({
          web3WalletType: Web3WalletType.MetaMask,
          otpType: 'Deposit',
          isRegLogPage,
        });
        break;

      default:
        break;
    }
  }

  /**点击事件集合 */
  clickBus: { [key: string]: Subscription } = {};
  /**监听子iframe发回的 click 事件 */
  handleBuildClick(item: OtherLogins, subscribeCallback = (e: MessageEvent<any>) => {}) {
    this.clickBus[item.name]?.unsubscribe();
    this.clickBus[item.name] = fromEvent<MessageEvent>(window, 'message')
      .pipe(untilDestroyed(this))
      .subscribe(e => {
        if (item.origin.includes(e.origin) && e.data === 'click') {
          subscribeCallback(e);
        }
      });
  }
}
